import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { getEnvVar } from '../../../environments/env-loader';

export interface SubUser {
  id: number;
  name:string;
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  birthDate: string;
  relationship?: string;
  country?: string;
  language?: string;
  status: 'ACTIVE' | 'PENDING' | 'DELETED';
  accessDate?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  permissions: UserPermission[];
  // password is NEVER included in API responses for security
}

export interface UserPermission {
  id: number;
  permission: string;
  accessLevel: 'FULL' | 'VIEW' | 'NONE';
  specificItems?: string[];
}

export interface CreateSubUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  password: string;
  birthDate: string;
  accessTiming?: 'immediate' | 'future';
  relationship?: string;
  country?: string;
  language?: string;
  accessDate?: string;
  permissions: {
    avatar: boolean;
    media: boolean;
    documents: boolean;
    chat: boolean;
    messages: boolean;
    diary: boolean;
    timeCapsule: boolean;
  };
  notes?: string;
}

export interface UpdateSubUserRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  birthDate?: string;
  relationship?: string;
  country?: string;
  language?: string;
  password?: string;
  accessDate?: string;
  permissions?: {
    avatar: boolean;
    media: boolean;
    documents: boolean;
    chat: boolean;
    messages: boolean;
  };
  notes?: string;
}

export interface SubUserListResponse {
  success: boolean;
  message: string;
  data: {
    subUsers: SubUser[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
    summary: {
      totalSubUsers: number;
      activeUsers: number;
      pendingUsers: number;
    };
  };
}

export interface SubUserResponse {
  success: boolean;
  message: string;
  data: SubUser;
}

@Injectable({
  providedIn: 'root'
})
export class SubUserService {
  private readonly API_URL = getEnvVar('REACT_APP_API_URL', environment.apiUrl);

  constructor(private http: HttpClient) {}

  /**
   * Get all sub-users for the current main user
   */
  getSubUsers(): Observable<SubUserListResponse> {
    return this.http.get<SubUserListResponse>(`${this.API_URL}/sub-users`);
  }

  /**
   * Get a specific sub-user by ID
   */
  getSubUser(id: number): Observable<SubUserResponse> {
    return this.http.get<SubUserResponse>(`${this.API_URL}/sub-users/${id}`);
  }

  /**
   * Create a new sub-user
   */
  createSubUser(userData: CreateSubUserRequest): Observable<SubUserResponse> {
    return this.http.post<SubUserResponse>(`${this.API_URL}/sub-users`, userData);
  }

  /**
   * Update an existing sub-user
   */
  updateSubUser(id: number, userData: UpdateSubUserRequest): Observable<SubUserResponse> {
    return this.http.put<SubUserResponse>(`${this.API_URL}/sub-users/${id}`, userData);
  }

  /**
   * Delete a sub-user
   */
  deleteSubUser(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(`${this.API_URL}/sub-users/${id}`);
  }

  /**
   * Send invitation to a sub-user
   */
  sendInvitation(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.API_URL}/sub-users/${id}/invite`, {});
  }

  /**
   * Calculate age from birth date
   */
  calculateAge(birthDate: string): number {
    if (!birthDate) return 0;
    
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  /**
   * Format last login time
   */
  formatLastLogin(lastLoginAt?: string): string {
    if (!lastLoginAt) return 'Never';
    
    const now = new Date();
    const lastLogin = new Date(lastLoginAt);
    const diffMs = now.getTime() - lastLogin.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  /**
   * Get permission status for display
   */
  getPermissionStatus(permission: UserPermission): { status: string; description: string } {
    switch (permission.accessLevel) {
      case 'FULL':
        return { status: 'Full Access', description: 'Complete access to all content' };
      case 'VIEW':
        return { status: 'View Only', description: `Limited access to ${permission.specificItems?.length || 0} items` };
      case 'NONE':
        return { status: 'No Access', description: 'No access granted' };
      default:
        return { status: 'Unknown', description: 'Access level not defined' };
    }
  }
}
